import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import Endpoints from './endpoints';
import { ensureAnonymousSessionId } from '@/lib/anonymous-session';
import { API_BASE_URL } from '@/lib/config';
import { restoreAdminSession } from '@/lib/impersonation';

const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';

const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // if (config.url?.startsWith(Endpoints.Chat.base())) {
      const clientId = ensureAnonymousSessionId();
      if (clientId) {
        config.headers['x-client-id'] = clientId;
      }
    // }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return httpClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (!refreshToken) {
        // An expired impersonation token has no refresh token: restore the admin session
        // instead of logging out entirely.
        if (restoreAdminSession()) {
          isRefreshing = false;
          window.location.href = '/admin';
          return Promise.reject(error);
        }
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem('auth_user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_BASE_URL}${Endpoints.Auth.refresh()}`, {
          refreshToken,
        });

        if (!data?.data || !data.data.token || !data.data.refreshToken) {
          throw new Error('Invalid refresh token response');
        }

        const { token: newToken, refreshToken: newRefreshToken } = data.data;

        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(REFRESH_KEY, newRefreshToken);

        httpClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

        processQueue(null, newToken);

        return httpClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        if (restoreAdminSession()) {
          window.location.href = '/admin';
          return Promise.reject(refreshError);
        }
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem('auth_user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default httpClient;
